interface Pitch {
  id: string;
  frames: any[];
  metadata: Record<string, any>;
}

interface ProcessedData {
  pitches: Record<string, Pitch>;
  pitchTypes: string[];
  domHands: string[]; // Unique dom_hand values from data
  dataColumns: string[]; // Metric columns (consistent across frames)
  timeSeriesColumns: string[]; // Time series columns (vary over time)
  globalMinTime: number;
  globalMaxTime: number;
}

// Helper function to get all columns from data
const getAllColumns = (data: any[]): string[] => {
  return data.length > 0 ? Object.keys(data[0]) : [];
};

// Helper function to check if column should be excluded
const isExcludedColumn = (col: string): boolean => {
  const excludedColumns = [
    "",
    "org_movement_id",
    "time",
    "event",
    "time_from_max_hand",
    "norm_time",
  ];
  return excludedColumns.includes(col);
};

// Helper function to initialize null column tracker
const initializeNullColumnTracker = (
  allColumns: string[],
): Record<string, boolean> => {
  const nullColumnTracker: Record<string, boolean> = {};

  for (const col of allColumns) {
    if (!isExcludedColumn(col)) {
      nullColumnTracker[col] = true; // Assume null until we find non-null value
    }
  }

  return nullColumnTracker;
};

// Initialize null column tracking
export const initializeColumnTracking = (data: any[]) => {
  const allColumns = getAllColumns(data);
  const nullColumnTracker = initializeNullColumnTracker(allColumns);

  return { allColumns, nullColumnTracker };
};

// Helper function to extract pitch ID from row data
const extractPitchId = (row: any): string => {
  return (
    row.org_movement_id ||
    row.movement_id ||
    row.id ||
    `movement_${row.movement_num || 0}`
  );
};

// Helper function to initialize pitch data if it doesn't exist
const initializePitchData = (
  processedPitchesData: Record<string, Pitch>,
  pitchId: string,
): void => {
  if (!processedPitchesData[pitchId]) {
    processedPitchesData[pitchId] = {
      id: pitchId,
      frames: [],
      metadata: {},
    };
  }
};

// Helper function to collect unique pitch types
const collectPitchType = (row: any, pitchTypesList: string[]): void => {
  if (
    row.mocap_pitch_type &&
    row.mocap_pitch_type !== "" &&
    !pitchTypesList.includes(row.mocap_pitch_type)
  ) {
    pitchTypesList.push(row.mocap_pitch_type);
  }
};

// Helper function to collect unique dom hands
const collectDomHand = (row: any, domHandsList: string[]): void => {
  if (
    row.dom_hand &&
    row.dom_hand !== "" &&
    !domHandsList.includes(row.dom_hand)
  ) {
    domHandsList.push(row.dom_hand);
  }
};

// Helper function to update null column tracking
const updateNullColumnTracking = (
  row: any,
  nullColumnTracker: Record<string, boolean>,
): void => {
  for (const col in nullColumnTracker) {
    if (row[col] !== null && row[col] !== undefined && row[col] !== "") {
      nullColumnTracker[col] = false; // Found non-null value
    }
  }
};

// Group data by pitch ID and collect metadata
export const groupDataByPitch = (
  data: any[],
  nullColumnTracker: Record<string, boolean>,
) => {
  const processedPitchesData: Record<string, Pitch> = {};
  const pitchTypesList: string[] = [];
  const domHandsList: string[] = [];
  const movementIds = new Set();

  for (const row of data) {
    const pitchId = extractPitchId(row);
    movementIds.add(pitchId);

    initializePitchData(processedPitchesData, pitchId);
    processedPitchesData[pitchId].frames.push(row);

    collectPitchType(row, pitchTypesList);
    collectDomHand(row, domHandsList);
    updateNullColumnTracking(row, nullColumnTracker);
  }

  return {
    processedPitchesData,
    pitchTypesList,
    domHandsList,
    movementIds,
  };
};

// Find consistent data columns (metric columns - same value across all frames)
export const findConsistentColumns = (
  processedPitchesData: Record<string, Pitch>,
  nullColumnTracker: Record<string, boolean>,
) => {
  const nonNullColumns = Object.keys(nullColumnTracker).filter(
    (col) => !nullColumnTracker[col],
  );

  return nonNullColumns.filter((col) => {
    // Check if column is consistent across all pitches
    return Object.values(processedPitchesData).every((pitch) => {
      if (pitch.frames.length <= 1) return true;

      const firstVal = pitch.frames[0][col];
      return pitch.frames.every((frame) => frame[col] === firstVal);
    });
  });
};

// Find time series columns (columns that vary over time across frames)
export const findTimeSeriesColumns = (
  processedPitchesData: Record<string, Pitch>,
  nullColumnTracker: Record<string, boolean>,
) => {
  const nonNullColumns = Object.keys(nullColumnTracker).filter(
    (col) => !nullColumnTracker[col],
  );

  return nonNullColumns.filter((col) => {
    // Check if column varies across frames in at least one pitch
    return Object.values(processedPitchesData).some((pitch) => {
      if (pitch.frames.length <= 1) return false;

      const firstVal = pitch.frames[0][col];
      // Column is a time series if it has different values across frames
      return pitch.frames.some((frame) => frame[col] !== firstVal);
    });
  });
};

// Calculate time bounds from data
export const calculateTimeBounds = (data: any[]) => {
  const times = data.map((row) => row.time_from_max_hand);
  return {
    globalMinTime: Math.min(...times),
    globalMaxTime: Math.max(...times),
  };
};

// Finalize pitch data with metadata and sorting
export const finalizePitchData = (
  processedPitchesData: Record<string, Pitch>,
  dataColumnsList: string[],
) => {
  Object.values(processedPitchesData).forEach((pitch) => {
    const firstFrame = pitch.frames[0];

    // Store metadata from first frame
    dataColumnsList.forEach((col) => {
      pitch.metadata[col] = firstFrame[col];
    });

    // Sort frames by time
    pitch.frames.sort((a, b) => a.time_from_max_hand - b.time_from_max_hand);
  });

  return processedPitchesData;
};

// Main data processing function
export const processVisualizationData = (data: any[]): ProcessedData => {
  if (!data || data.length === 0) {
    return {
      pitches: {},
      pitchTypes: [],
      domHands: [],
      dataColumns: [],
      timeSeriesColumns: [],
      globalMinTime: 0,
      globalMaxTime: 1,
    };
  }

  // Initialize column tracking
  const { nullColumnTracker } = initializeColumnTracking(data);

  // Group data by pitch and collect metadata
  const { processedPitchesData, pitchTypesList, domHandsList } =
    groupDataByPitch(data, nullColumnTracker);

  // Find consistent columns (metric columns)
  const dataColumnsList = findConsistentColumns(
    processedPitchesData,
    nullColumnTracker,
  );

  // Find time series columns (columns that vary over time)
  const timeSeriesColumnsList = findTimeSeriesColumns(
    processedPitchesData,
    nullColumnTracker,
  );

  // Calculate time bounds
  const { globalMinTime, globalMaxTime } = calculateTimeBounds(data);

  // Finalize pitch data
  const finalPitches = finalizePitchData(processedPitchesData, dataColumnsList);

  return {
    pitches: finalPitches,
    pitchTypes: pitchTypesList.sort(),
    domHands: domHandsList.sort(),
    dataColumns: dataColumnsList,
    timeSeriesColumns: timeSeriesColumnsList,
    globalMinTime,
    globalMaxTime,
  };
};
